import React from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { Button, Card, Descriptions, Spin, Typography, Space, Tag } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import workOrderService from '@/services/workOrder.service';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';

const { Title, Text } = Typography;

const ENTITY_INLAND_PROCESS_DEVIATION = 'inland_process_deviation';

const InlandProcessDeviationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const backToListPath = location.pathname.startsWith('/growth/') ? '/growth/inland-process-deviations' : null;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['inland-process-deviation', id],
    queryFn: () => workOrderService.getInlandProcessDeviation(id!),
    enabled: !!id?.trim(),
  });

  if (!id?.trim()) {
    return <Text type="danger">Missing deviation id</Text>;
  }

  if (isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ padding: 24 }}>
        <Text type="danger">{(error as Error)?.message || 'Could not load deviation'}</Text>
      </div>
    );
  }

  const snap = data.boq_snapshot_json as Record<string, unknown> | undefined;
  const prPayload = data.pr_payload_json as Record<string, unknown> | undefined;

  return (
    <div style={{ padding: 24 }}>
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => (backToListPath ? navigate(backToListPath) : navigate(-1))}
        style={{ marginBottom: 16 }}
      >
        Back
      </Button>
      <Title level={4} style={{ marginBottom: 8 }}>
        Inland process deviation {data.deviation_number ? <Text code>{String(data.deviation_number)}</Text> : null}
      </Title>
      <Space style={{ marginBottom: 16 }}>
        <Tag>{String(data.status ?? '')}</Tag>
        {data.generated_purchase_requisition_id ? (
          <Link to={`/scm/procurement/purchase-requests/${String(data.generated_purchase_requisition_id)}`}>
            {typeof data.pr_number === 'string' && data.pr_number.trim()
              ? `PR ${data.pr_number.trim()}`
              : 'Open generated PR'}
          </Link>
        ) : null}
      </Space>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label="Work order">
            {data.work_order_id ? (
              <Link to={`/mfg/work-orders/${String(data.work_order_id)}`}>
                {typeof data.work_order_number === 'string' && data.work_order_number.trim()
                  ? data.work_order_number.trim()
                  : 'Open work order'}
              </Link>
            ) : (
              '—'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Justification">{String(data.justification_reason ?? '—')}</Descriptions.Item>
          <Descriptions.Item label="Category">{String(data.justification_category ?? '—')}</Descriptions.Item>
          <Descriptions.Item label="Proposed outsource cost">
            {data.proposed_outsource_cost != null ? String(data.proposed_outsource_cost) : '—'}
          </Descriptions.Item>
          {data.pr_generation_error ? (
            <Descriptions.Item label="PR generation (after approval)">
              <Text type="danger">{String(data.pr_generation_error)}</Text>
            </Descriptions.Item>
          ) : null}
        </Descriptions>
      </Card>

      {snap ? (
        <Card size="small" title="BOQ cost snapshot (at request)" style={{ marginBottom: 16 }}>
          <pre style={{ margin: 0, fontSize: 12, overflow: 'auto' }}>{JSON.stringify(snap, null, 2)}</pre>
        </Card>
      ) : null}

      {prPayload ? (
        <Card size="small" title="PR payload (submitted)" style={{ marginBottom: 16 }}>
          <pre style={{ margin: 0, fontSize: 12, overflow: 'auto' }}>{JSON.stringify(prPayload, null, 2)}</pre>
        </Card>
      ) : null}

      <ApprovalPanel
        entityType={ENTITY_INLAND_PROCESS_DEVIATION}
        entityId={id}
      />

      <Button style={{ marginTop: 16 }} onClick={() => refetch()}>
        Refresh
      </Button>
    </div>
  );
};

export default InlandProcessDeviationDetailPage;
